import { useQuery } from '@tanstack/react-query';
import { ExternalLink, History } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { RecentAdditionDto } from '@/Domain/api/KnowledgeBase/dto/SyncedSite.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { Spinner } from '@/Domain/components/Shared/Spinner';

const RECENT_ADDITIONS_LIMIT = 20;

type RecentAdditionsPopoverProps = {
  siteId: number;
};

export const RecentAdditionsPopover = ({ siteId }: RecentAdditionsPopoverProps) => {
  const { t } = useTranslation('knowledge_base');
  const [open, setOpen] = useState(false);
  const query = useQuery<RecentAdditionDto[]>({
    queryKey: [QueryKey.knowledgeBase.syncedSites.recentAdditions, { siteId }],
    enabled: open,
  });

  const items = Array.isArray(query.data) ? query.data : [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="xs"
          aria-label={t('synced_sites.actions.recent_additions')}
          data-testid={`synced-site-recent-${siteId}`}
        >
          <History className="size-3.5" />
          {t('synced_sites.actions.recent_additions')}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0"
        data-testid={`synced-site-recent-popover-${siteId}`}
      >
        <div className="flex flex-col gap-1 border-b border-border p-3">
          <span className="text-sm font-semibold text-foreground">
            {t('synced_sites.recent_additions.title')}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('synced_sites.recent_additions.description')}
          </span>
        </div>

        {query.isLoading ? (
          <div className="flex items-center justify-center p-6">
            <Spinner className="size-4" />
          </div>
        ) : items.length === 0 ? (
          <p className="p-4 text-xs text-muted-foreground">
            {t('synced_sites.recent_additions.empty')}
          </p>
        ) : (
          <ul className="max-h-64 divide-y divide-border overflow-y-auto">
            {items.map((item) => (
              <li key={item.id}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-accent"
                  title={item.url}
                >
                  <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                    {item.title || t('synced_sites.recent_additions.untitled')}
                  </span>
                  <ExternalLink className="size-3 shrink-0 text-muted-foreground" />
                </a>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-col gap-1 border-t border-border p-3">
          {items.length >= RECENT_ADDITIONS_LIMIT && (
            <span className="text-xs text-foreground-subtle">
              {t('synced_sites.recent_additions.limit_note', { count: RECENT_ADDITIONS_LIMIT })}
            </span>
          )}
          <span className="text-xs text-foreground-subtle">
            {t('synced_sites.recent_additions.removed_note')}
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
